import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ShieldCheck, Fingerprint, Wallet, Trash2, AlertTriangle } from 'lucide-react';
import { api } from '../../api/client';

interface UserDetail {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  isAdmin: boolean;
  isSuspended: boolean;
  emailVerified: boolean;
  is2faEnabled: boolean;
  isBiometricEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  kycVerification: { verificationStatus: string; kycLevel: string } | null;
  hcaAccount: { accountNumber: string; balance: string | number; accountStatus: string } | null;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border/60 last:border-0">
      <span className="text-textTertiary text-sm">{label}</span>
      <span className="text-textPrimary text-sm font-medium">{value}</span>
    </div>
  );
}

export default function AdminUserDrawer({
  userId,
  onClose,
  onDeleted,
}: {
  userId: number | null;
  onClose: () => void;
  onDeleted?: (id: number) => void;
}) {
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (userId == null) return;
    setLoading(true);
    setUser(null);
    setConfirming(false);
    setConfirmText('');
    setDeleteError(null);
    api
      .get(`/admin/users/${userId}`)
      .then((res) => setUser(res.data))
      .finally(() => setLoading(false));
  }, [userId]);

  const handleDelete = async () => {
    if (!user) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete(`/admin/users/${user.id}`);
      onDeleted?.(user.id);
      onClose();
    } catch (err: any) {
      setDeleteError(err?.response?.data?.message || 'Failed to delete user.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AnimatePresence>
      {userId != null && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-surface1 border-l border-border z-50 overflow-y-auto"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-border">
              <h2 className="font-display text-lg font-bold text-textPrimary">User Detail</h2>
              <button onClick={onClose} className="text-textTertiary hover:text-textPrimary">
                <X size={20} />
              </button>
            </div>

            {loading || !user ? (
              <div className="p-6 text-textSecondary">Loading...</div>
            ) : (
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-textPrimary font-bold text-xl mb-1">
                    {user.firstName} {user.lastName}
                  </h3>
                  <p className="text-textSecondary text-sm">{user.email}</p>
                  {user.isAdmin && (
                    <span className="inline-block mt-2 text-xs bg-gold-gradient text-bg px-2 py-0.5 rounded-full font-semibold">ADMIN</span>
                  )}
                </div>

                <div>
                  <h4 className="text-textTertiary text-xs font-bold uppercase tracking-wide mb-2">Profile</h4>
                  <Row label="Phone" value={user.phoneNumber || '—'} />
                  <Row label="Email Verified" value={user.emailVerified ? 'Yes' : 'No'} />
                  <Row label="Joined" value={new Date(user.createdAt).toLocaleDateString()} />
                  <Row label="Status" value={user.isSuspended ? 'Suspended' : 'Active'} />
                </div>

                <div>
                  <h4 className="text-textTertiary text-xs font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <ShieldCheck size={13} /> Security
                  </h4>
                  <Row label="2FA Enabled" value={user.is2faEnabled ? 'Yes' : 'No'} />
                  <Row label="Biometric" value={user.isBiometricEnabled ? 'Yes' : 'No'} />
                </div>

                <div>
                  <h4 className="text-textTertiary text-xs font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Fingerprint size={13} /> KYC
                  </h4>
                  <Row label="Status" value={user.kycVerification?.verificationStatus ?? 'PENDING'} />
                  <Row label="Level" value={user.kycVerification?.kycLevel ?? '—'} />
                </div>

                {user.hcaAccount && (
                  <div>
                    <h4 className="text-textTertiary text-xs font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5">
                      <Wallet size={13} /> HCA Account
                    </h4>
                    <Row label="Account Number" value={user.hcaAccount.accountNumber} />
                    <Row label="Balance" value={`$${Number(user.hcaAccount.balance).toLocaleString()}`} />
                    <Row label="Status" value={user.hcaAccount.accountStatus} />
                  </div>
                )}

                {!user.isAdmin && (
                  <div className="pt-4 border-t border-error/20">
                    <h4 className="text-error text-xs font-bold uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <AlertTriangle size={13} /> Danger Zone
                    </h4>
                    {!confirming ? (
                      <button
                        onClick={() => {
                          setConfirming(true);
                          setDeleteError(null);
                        }}
                        className="w-full flex items-center justify-center gap-2 text-sm px-3 py-2.5 rounded-lg bg-error/10 hover:bg-error/20 text-error font-medium transition"
                      >
                        <Trash2 size={15} /> Delete User
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-textSecondary text-sm">
                          This permanently deletes the user and{' '}
                          <span className="text-textPrimary font-medium">all related data</span> — HCA account,
                          transactions, cards, KYC, sessions and more. This cannot be undone.
                        </p>
                        <p className="text-textTertiary text-xs">
                          Type <span className="text-textPrimary font-mono">{user.email}</span> to confirm.
                        </p>
                        <input
                          value={confirmText}
                          onChange={(e) => setConfirmText(e.target.value)}
                          placeholder="Confirm email"
                          autoFocus
                          className="w-full px-3 py-2 bg-surface2 border border-border rounded-lg text-textPrimary text-sm focus:outline-none focus:border-error"
                        />
                        {deleteError && <p className="text-error text-xs">{deleteError}</p>}
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              setConfirming(false);
                              setConfirmText('');
                              setDeleteError(null);
                            }}
                            disabled={deleting}
                            className="flex-1 text-sm px-3 py-2 rounded-lg bg-surface2 border border-border text-textSecondary hover:text-textPrimary transition disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleDelete}
                            disabled={deleting || confirmText !== user.email}
                            className="flex-1 flex items-center justify-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-error hover:bg-error/90 text-white font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Trash2 size={14} /> {deleting ? 'Deleting…' : 'Permanently Delete'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
